import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import SunEditor from "suneditor-react";

import { toast } from "react-toastify";
import { update } from "../api";
import { CMSSchema } from "../validations";
import styles from "../styles/cms.module.css";
import { CMSTypes } from "../../../interfaces";
import { CMS_TYPE } from "../../../lib/constants";
import { CONSTANTS } from "../../../lib/constants/messages";
import { IMAGES } from "../../../assets/images";

interface Props {
  cms: CMSTypes.SpecificCMS;
  cmsType: number;
  loading: boolean;
  resetCMS: () => void;
};

const PrivacyPolicy = ({ cms, cmsType, loading, resetCMS }: Props) => {
  const [isEditor, toggleEditor] = useState<boolean>(false);

  // Close editor when switching between different CMS types
  useEffect(() => {
    if (isEditor) {
      toggleEditor(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsType]);

  // SunEditor configuration with comprehensive toolbar
  const editorOptions = {
    buttonList: [
      ['undo', 'redo'],
      ['font', 'fontSize', 'formatBlock'],
      ['paragraphStyle', 'blockquote'],
      ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
      ['fontColor', 'hiliteColor', 'textStyle'],
      ['removeFormat'],
      ['outdent', 'indent'],
      ['align', 'horizontalRule', 'list', 'lineHeight'],
      ['table', 'link', 'image', 'video', 'audio'],
      ['fullScreen', 'showBlocks', 'codeView'],
      ['preview', 'print'],
      ['save', 'template']
    ],
    fontSize: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72],
    font: [
      'Arial', 'Calibri', 'Comic Sans MS', 'Courier New',
      'Georgia', 'Impact', 'Lucida Console', 'Tahoma',
      'Times New Roman', 'Trebuchet MS', 'Verdana'
    ],
    minHeight: '300px',
    maxHeight: '600px',
    placeholder: 'Start writing your content...',
    showPathLabel: false,
    charCounter: false,
    width: '100%',
    height: 'auto',
    defaultStyle: 'font-family: Arial, sans-serif; font-size: 14px; color: #ffffff; background-color: #000000;',
    theme: 'dark'
  };


  const cmsFormik = useFormik({
    initialValues: { content: cms && cms.content },
    onSubmit: (values, { setSubmitting }) => {
      const payload = { content: values.content as string, type: cmsType };

      toast.promise(
        update(payload),
        {
          pending: {
            render() {
              return CONSTANTS.CMS.UPDATING;
            },
          },
          success: {
            render() {
              toggleEditor(false);

              resetCMS();

              return CONSTANTS.CMS.SUCCESS;
            },
          },
          error: {
            render() {
              setSubmitting(false);

              return CONSTANTS.CMS.FAILED;
            },
          },
        });
    },
    enableReinitialize: true,
    validationSchema: CMSSchema,
  });

  return (
    <div>
      <form
        onSubmit={cmsFormik.handleSubmit}
      >
        <div className={styles.tabDtahd}>
          <h4>{cmsType === CMS_TYPE.PRIVACY_POLICY ? "Privacy Policy" : cmsType === CMS_TYPE.TERMS_CONDITIONS ? "Terms & Conditions" : cmsType === CMS_TYPE.ABOUT_US ? "About Us" : cmsType === CMS_TYPE.HOW_TO_USE ? "How to use" : cmsType === CMS_TYPE.REFUND_POLICY ? "Refund Policy" : cmsType === CMS_TYPE.SHIPPIN_POLICY ? "Shipping Policy" : "CMS Content"}</h4>
          <div>
            {isEditor && (
              <button type="submit" className="me-2">
                <img
                  width={100}
                  height={100}
                  src={IMAGES.EditIcon}
                  alt="defaultUser"
                />
                Update
              </button>
            )}
            <button type="button" onClick={() => toggleEditor(!isEditor)}>
              <img
                width={100}
                height={100}
                src={IMAGES.EditIcon}
                alt="defaultUser"
              />
              {isEditor ? "Cancel" : "Edit"}
            </button>
          </div>
        </div>

        {loading ? <p className={styles.loading}>Loading...</p> : null}

        {!loading && <div className={styles.tabData}>
          {cms && cms.content && !isEditor && !loading ? (
            <div
              dangerouslySetInnerHTML={{
                __html: cms.content,
              }}
            />
          ) : null}
          {isEditor ? (
            <div className={styles.sunEditorWrapper} style={{ backgroundColor: '#000000', color: '#ffffff' }}>
              <SunEditor
                key={`suneditor-${cmsType}-${cms?._id || 'new'}`}
                setContents={cmsFormik.values.content || ""}
                onChange={(content: any) => cmsFormik.setFieldValue("content", content)}
                setOptions={editorOptions}
              />
            </div>
          ) : null}
          {isEditor &&
            cmsFormik.touched.content &&
            cmsFormik.errors.content ? (
            <p className={styles.error}>{cmsFormik.errors.content}</p>
          ) : null}
        </div>}
      </form>
    </div>
  );
};

export default PrivacyPolicy;